import { useCallback, useState } from 'react';
import { useAppSelector } from '@/shared/lib/redux/hooks';
import type { CartItem, CartState } from '../model/types';
import { EMPTY_CART, readCart, writeCart } from './cartStorage';

/** §9 [ПРЕДЛ]: корзина заявки на товар — количество только в коробках, хранится в localStorage
 *  по паре «магазин + учётная запись» (без сервера — заявка отправляется целиком через
 *  `createOrderRequest`). Без учётной записи корзина живёт только в памяти вкладки. */
export function useClientCart(storeId: string) {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const owner = userId ? `${storeId}:${userId}` : null;
  const load = (): CartState => (userId ? readCart(storeId, userId) : EMPTY_CART);

  const [cart, setCart] = useState<CartState>(load);
  // Сменился магазин или учётная запись — показываем корзину нового владельца, а не прежнюю
  // (render-phase, как и остальная подстройка состояния под пропсы в проекте).
  const [loadedOwner, setLoadedOwner] = useState(owner);
  if (owner !== loadedOwner) {
    setLoadedOwner(owner);
    setCart(load());
  }

  const persist = useCallback(
    (next: CartState) => {
      setCart(next);
      if (userId) writeCart(storeId, userId, next);
    },
    [storeId, userId],
  );

  const addItem = useCallback(
    (item: CartItem) => {
      persist({
        ...cart,
        items: cart.items.some((i) => i.categoryId === item.categoryId)
          ? cart.items.map((i) => (i.categoryId === item.categoryId ? { ...i, boxes: i.boxes + item.boxes } : i))
          : [...cart.items, item],
      });
    },
    [cart, persist],
  );

  const setBoxes = useCallback(
    (categoryId: string, boxes: number) => {
      persist({ ...cart, items: cart.items.map((i) => (i.categoryId === categoryId ? { ...i, boxes } : i)) });
    },
    [cart, persist],
  );

  const removeItem = useCallback(
    (categoryId: string) => {
      persist({ ...cart, items: cart.items.filter((i) => i.categoryId !== categoryId) });
    },
    [cart, persist],
  );

  const setComment = useCallback(
    (comment: string) => {
      persist({ ...cart, comment });
    },
    [cart, persist],
  );

  const clear = useCallback(() => {
    persist(EMPTY_CART);
  }, [persist]);

  return { cart, addItem, setBoxes, removeItem, setComment, clear };
}
